import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronRight,
  ChevronDown,
  FileText,
  Hash,
  BookOpen,
  AlertCircle,
} from 'lucide-react';

export interface Clause {
  id: string;
  number: string;
  text: string;
  hasRisk?: boolean;
  riskSeverity?: 'critical' | 'high' | 'medium' | 'low';
  hasCfrCitation?: boolean;
}

export interface Section {
  id: string;
  number: string;
  title: string;
  clauses: Clause[];
}

export interface Article {
  id: string;
  number: string;
  title: string;
  summary?: string;
  sections: Section[];
  riskCount?: number;
}

export interface ArticleSectionTreeProps {
  articles: Article[];
  isLoading?: boolean;
  onClauseClick?: (clause: Clause, section: Section, article: Article) => void;
  onArticleClick?: (article: Article) => void;
  highlightClause?: string | null; // Format: "Article X, Section X.X, Clause X.X.X"
}

const riskSeverityColors: Record<string, string> = {
  critical: 'text-red-600 bg-red-100',
  high: 'text-orange-600 bg-orange-100',
  medium: 'text-yellow-600 bg-yellow-100',
  low: 'text-green-600 bg-green-100',
};

const TreeSkeleton: React.FC = () => (
  <div className="space-y-2 animate-pulse">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="p-3 bg-gray-100 rounded-lg">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
        <div className="h-3 bg-gray-200 rounded w-1/2" />
      </div>
    ))}
  </div>
);

const ClauseItem: React.FC<{
  clause: Clause;
  section: Section;
  article: Article;
  onClick?: (clause: Clause, section: Section, article: Article) => void;
  isHighlighted?: boolean;
  highlightRef?: React.RefObject<HTMLDivElement | null>;
}> = ({ clause, section, article, onClick, isHighlighted, highlightRef }) => (
  <div
    ref={isHighlighted ? highlightRef : undefined}
    className={`py-2 px-3 ml-8 border-l-2 hover:bg-gray-50 cursor-pointer transition-all ${
      clause.hasRisk ? 'border-l-warning-400' : 'border-gray-200'
    } ${isHighlighted ? 'bg-primary-50 border-l-primary-500 ring-2 ring-primary-300 ring-inset rounded-r-md' : ''}`}
    onClick={() => onClick?.(clause, section, article)}
    data-testid={`clause-${clause.id}`}
  >
    <div className="flex items-start gap-2">
      <Hash className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-medium text-gray-500">
            Clause {clause.number}
          </span>
          {clause.hasRisk && clause.riskSeverity && (
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                riskSeverityColors[clause.riskSeverity]
              }`}
              data-testid={`clause-risk-${clause.id}`}
            >
              <AlertCircle className="h-3 w-3 mr-1" />
              {clause.riskSeverity}
            </span>
          )}
          {clause.hasCfrCitation && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700"
              data-testid={`clause-cfr-${clause.id}`}
            >
              CFR
            </span>
          )}
        </div>
        <p className="text-sm text-gray-700 line-clamp-2">{clause.text}</p>
      </div>
    </div>
  </div>
);

const SectionItem: React.FC<{
  section: Section;
  article: Article;
  isExpanded: boolean;
  onToggle: () => void;
  onClauseClick?: (clause: Clause, section: Section, article: Article) => void;
  highlightedClauseNumber?: string;
  highlightRef?: React.RefObject<HTMLDivElement | null>;
}> = ({ section, article, isExpanded, onToggle, onClauseClick, highlightedClauseNumber, highlightRef }) => (
  <div className="ml-4" data-testid={`section-${section.id}`}>
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-2 py-2 px-3 hover:bg-gray-50 rounded-md transition-colors"
      aria-expanded={isExpanded}
      data-testid={`section-toggle-${section.id}`}
    >
      {section.clauses.length > 0 ? (
        isExpanded ? (
          <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-500 flex-shrink-0" />
        )
      ) : (
        <div className="w-4" />
      )}
      <BookOpen className="h-4 w-4 text-gray-400 flex-shrink-0" />
      <span className="text-sm font-medium text-gray-700 truncate">
        Section {section.number}: {section.title}
      </span>
      {section.clauses.length > 0 && (
        <span className="ml-auto text-xs text-gray-400">
          {section.clauses.length} clause{section.clauses.length !== 1 ? 's' : ''}
        </span>
      )}
    </button>
    {isExpanded && section.clauses.length > 0 && (
      <div className="space-y-1 py-1">
        {section.clauses.map((clause) => (
          <ClauseItem
            key={clause.id}
            clause={clause}
            section={section}
            article={article}
            onClick={onClauseClick}
            isHighlighted={clause.number === highlightedClauseNumber}
            highlightRef={clause.number === highlightedClauseNumber ? highlightRef : undefined}
          />
        ))}
      </div>
    )}
  </div>
);

const ArticleItem: React.FC<{
  article: Article;
  isExpanded: boolean;
  expandedSections: Set<string>;
  onToggle: () => void;
  onSectionToggle: (sectionId: string) => void;
  onArticleClick?: (article: Article) => void;
  onClauseClick?: (clause: Clause, section: Section, article: Article) => void;
  highlightedSectionNumber?: string;
  highlightedClauseNumber?: string;
  highlightRef?: React.RefObject<HTMLDivElement | null>;
}> = ({
  article,
  isExpanded,
  expandedSections,
  onToggle,
  onSectionToggle,
  onArticleClick,
  onClauseClick,
  highlightedSectionNumber,
  highlightedClauseNumber,
  highlightRef,
}) => (
  <div
    className="border border-gray-200 rounded-lg overflow-hidden"
    data-testid={`article-${article.id}`}
  >
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
      aria-expanded={isExpanded}
      data-testid={`article-toggle-${article.id}`}
    >
      {isExpanded ? (
        <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
      ) : (
        <ChevronRight className="h-5 w-5 text-gray-500 flex-shrink-0" />
      )}
      <FileText className="h-5 w-5 text-primary-600 flex-shrink-0" />
      <div className="flex-1 text-left min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-900">
            Article {article.number}
          </span>
          {article.riskCount && article.riskCount > 0 && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-700"
              data-testid={`article-risk-count-${article.id}`}
            >
              <AlertCircle className="h-3 w-3 mr-1" />
              {article.riskCount} risk{article.riskCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 truncate">{article.title}</p>
      </div>
      <span className="text-xs text-gray-400 flex-shrink-0">
        {article.sections.length} section{article.sections.length !== 1 ? 's' : ''}
      </span>
    </button>

    {isExpanded && (
      <div className="p-4 pt-2 space-y-2 border-t border-gray-200">
        {article.summary && (
          <p
            className="text-sm text-gray-600 italic mb-3 pb-3 border-b border-gray-100"
            data-testid={`article-summary-${article.id}`}
          >
            {article.summary}
          </p>
        )}
        {article.sections.length === 0 ? (
          <p className="text-sm text-gray-400 italic py-2">No sections found</p>
        ) : (
          article.sections.map((section) => (
            <SectionItem
              key={section.id}
              section={section}
              article={article}
              isExpanded={expandedSections.has(section.id)}
              onToggle={() => onSectionToggle(section.id)}
              onClauseClick={onClauseClick}
              highlightedClauseNumber={section.number === highlightedSectionNumber ? highlightedClauseNumber : undefined}
              highlightRef={section.number === highlightedSectionNumber ? highlightRef : undefined}
            />
          ))
        )}
        {onArticleClick && (
          <button
            onClick={() => onArticleClick(article)}
            className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
            data-testid={`article-details-link-${article.id}`}
          >
            View Article Details
          </button>
        )}
      </div>
    )}
  </div>
);

// Parse clause reference like "Article 11, Section 11.1, Clause 11.1.1"
const parseClauseReference = (ref: string): { articleNumber: string; sectionNumber: string; clauseNumber: string } | null => {
  const match = ref.match(/Article\s+(\d+),?\s*Section\s+([\d.]+),?\s*Clause\s+([\d.]+)/i);
  if (match) {
    return {
      articleNumber: match[1],
      sectionNumber: match[2],
      clauseNumber: match[3],
    };
  }
  return null;
};

export const ArticleSectionTree: React.FC<ArticleSectionTreeProps> = ({
  articles,
  isLoading,
  onClauseClick,
  onArticleClick,
  highlightClause,
}) => {
  const [expandedArticles, setExpandedArticles] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const highlightRef = useRef<HTMLDivElement>(null);

  // Parse the highlight clause reference
  const parsedHighlight = highlightClause ? parseClauseReference(highlightClause) : null;

  // Auto-expand and scroll to highlighted clause
  useEffect(() => {
    if (parsedHighlight && articles.length > 0) {
      // Find the article and section to expand
      const article = articles.find(a => a.number === parsedHighlight.articleNumber);
      if (article) {
        const section = article.sections.find(s => s.number === parsedHighlight.sectionNumber);
        if (section) {
          // Expand the article and section
          setExpandedArticles(prev => new Set([...Array.from(prev), article.id]));
          setExpandedSections(prev => new Set([...Array.from(prev), section.id]));

          // Scroll to the highlighted clause after a short delay for render
          setTimeout(() => {
            highlightRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }, 300);
        }
      }
    }
  }, [highlightClause, parsedHighlight, articles]);

  const toggleArticle = (articleId: string) => {
    setExpandedArticles((prev) => {
      const next = new Set(prev);
      if (next.has(articleId)) {
        next.delete(articleId);
      } else {
        next.add(articleId);
      }
      return next;
    });
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedArticles(new Set(articles.map((a) => a.id)));
    const allSections = articles.flatMap((a) => a.sections.map((s) => s.id));
    setExpandedSections(new Set(allSections));
  };

  const collapseAll = () => {
    setExpandedArticles(new Set());
    setExpandedSections(new Set());
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6" data-testid="article-tree-loading">
        <TreeSkeleton />
      </div>
    );
  }

  if (articles.length === 0) {
    return (
      <div
        className="bg-white rounded-lg shadow-md p-8 text-center"
        data-testid="article-tree-empty"
      >
        <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
        <h3 className="text-lg font-medium text-gray-900 mb-1">
          No Structure Available
        </h3>
        <p className="text-sm text-gray-500">
          Process the document to extract its structure.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md" data-testid="article-section-tree">
      {/* Header with controls */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Contract Structure
          </h2>
          <p className="text-sm text-gray-500">
            {articles.length} article{articles.length !== 1 ? 's' : ''},{' '}
            {articles.reduce((sum, a) => sum + a.sections.length, 0)} section
            {articles.reduce((sum, a) => sum + a.sections.length, 0) !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={expandAll}
            className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            data-testid="expand-all"
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            data-testid="collapse-all"
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* Tree content */}
      <div className="p-4 space-y-3">
        {articles.map((article) => (
          <ArticleItem
            key={article.id}
            article={article}
            isExpanded={expandedArticles.has(article.id)}
            expandedSections={expandedSections}
            onToggle={() => toggleArticle(article.id)}
            onSectionToggle={toggleSection}
            onArticleClick={onArticleClick}
            onClauseClick={onClauseClick}
            highlightedSectionNumber={article.number === parsedHighlight?.articleNumber ? parsedHighlight?.sectionNumber : undefined}
            highlightedClauseNumber={article.number === parsedHighlight?.articleNumber ? parsedHighlight?.clauseNumber : undefined}
            highlightRef={article.number === parsedHighlight?.articleNumber ? highlightRef : undefined}
          />
        ))}
      </div>
    </div>
  );
};
